<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { api } from "@/api/server";
import type { EventCount } from "@/types/protocol";

const props = defineProps<{ days: number }>();

const REFRESH_MS = 60_000;

/**
 * Nur die Ereignisarten, die für eine Auswertung etwas hergeben. An-/Abmeldungen und
 * Verbindungswechsel stehen zwar im Log, ergäben hier aber nur große Zahlen ohne Aussage.
 */
const shown: ReadonlyArray<readonly [string, string]> = [
  ["BudgetOver", "Budget aufgebraucht"],
  ["Budget", "Vorwarnungen"],
  ["SchoolMode", "Schulmodus an"],
  ["SchoolModeOff", "Schulmodus aus"],
  ["SchoolModeRelease", "Aufgaben bestätigt"],
  ["CommandSent", "Kommandos vom Dashboard"],
];

const counts = ref<EventCount[]>([]);
let timer: number | undefined;

const tiles = computed(() =>
  shown
    .map(([kind, label]) => ({ label, count: counts.value.find((c) => c.kind === kind)?.count ?? 0 }))
    .filter((tile) => tile.count > 0),
);

async function load(): Promise<void> {
  try {
    counts.value = await api.eventSummary(props.days);
  } catch {
    // Rein ergänzende Anzeige - ein Fehler hier darf den Verlauf nicht stören.
    counts.value = [];
  }
}

onMounted(() => {
  void load();
  timer = window.setInterval(() => void load(), REFRESH_MS);
});

onBeforeUnmount(() => window.clearInterval(timer));

watch(() => props.days, () => void load());
</script>

<template>
  <div v-if="tiles.length" class="tiles">
    <div v-for="tile in tiles" :key="tile.label" class="tile">
      <span class="value">{{ tile.count }}</span>
      <span class="label">{{ tile.label }}</span>
    </div>
  </div>
</template>

<style scoped>
.tiles {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.tile {
  display: flex;
  align-items: baseline;
  gap: 7px;
  padding: 6px 11px;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  background: var(--panel);
  font-size: 12.5px;
  color: var(--muted);
}

.value {
  font-size: 16px;
  font-weight: 600;
  color: var(--fg);
}
</style>
